import axiosInstance from '../utils/axiosInstance';

export async function getCompanies(params){
    const { data } = await axiosInstance.get('/super-admin/companies', { params });
    return Array.isArray(data) ? data : data?.data ?? [];
}

export async function getDashboardStats() {
    const { data } = await axiosInstance.get('/super-admin/dashboard/stats');
    return Array.isArray(data) ? data : data?.data ?? [];
}

export async function createCompany(payload) {
    const { data } = await axiosInstance.post('/super-admin/company', {payload});
    return data;
}

export async function getCompanyById(companyId) {
    const { data } = await axiosInstance.get('/super-admin/company', { params : {companyId} });
    return Array.isArray(data) ? data : data?.data ?? [];
}

export async function editCompany(companyId, payload) {
    const { data } = await axiosInstance.put('/super-admin/company', payload, { params: { companyId }});
    return data;
}

export const deleteCompany = async (id) => {
    const { data } = await axiosInstance.delete("/super-admin/company/:id");
    return data;
}

export const sendOtpToSuperAdmin = async () => {
    const { data } = await axiosInstance.post("/super-admin/sendOTP");
    return data;
}

export async function verifyOtp(otp, id) {
    const res = await axiosInstance.post("/super-admin/verifyOTP", { otp, id });
    return res;
}

export async function markCompanyAsPaid(id, subscriptionStartDate) {
    const res = await axiosInstance.put(`/super-admin/markCompanyAsPaid/${id}`, {subscriptionStartDate: subscriptionStartDate});
    return res;
}

// Re-send the company admin's original invitation (only while they haven't signed up).
export async function resendCompanyAdminInvite(companyId) {
    const { data } = await axiosInstance.post(`/super-admin/companies/${companyId}/resend-invite`);
    return data?.data ?? data;
}

// Seat requests raised by company-admins (off-platform seat top-ups)
export async function getSeatRequests(params) {
    const { data } = await axiosInstance.get('/super-admin/seat-requests', { params });
    return data;
}

export async function resolveSeatRequest(payload) {
    // payload: { requestId, action: 'approve'|'reject', approved_seats?, note? }
    const { data } = await axiosInstance.put('/super-admin/seat-requests/resolve', payload);
    return data;
}

// Grant seats or switch the plan directly, with no company-admin request to
// approve. Offline-billed companies only — the backend rejects any company
// with a Razorpay subscription.
// payload: { type: 'seats'|'plan_upgrade', seats?, plan_id?, note? }
export async function provisionCompany(companyId, payload) {
    const { data } = await axiosInstance.post(`/super-admin/companies/${companyId}/provision`, payload);
    return data?.data ?? data;
}

// Price a provision WITHOUT applying it — same payload as provisionCompany.
// Runs the server's own pricing path and stops before the first write, so the
// figure the dialog previews is the figure the invoice is raised for (and a
// refusal here is the same refusal apply would give).
export async function quoteProvision(companyId, payload) {
    const { data } = await axiosInstance.post(`/super-admin/companies/${companyId}/provision/quote`, payload);
    return data?.data ?? data;
}

// Record a payment collected outside the platform so it appears in Purchase
// History. Offline-billed companies only — the backend refuses a company whose
// charges come from Razorpay. Plan/cycle/seats are NOT sent: the server reads
// them off the subscription so a typo here cannot file a purchase against a
// plan the company was never on.
// payload: { paid_on, purchase_type, amount?, note? }
export async function addCompanyPurchase(companyId, payload) {
    const { data } = await axiosInstance.post(`/super-admin/companies/${companyId}/purchase`, payload);
    return data?.data ?? data;
}

// Mark a pending OFFLINE invoice as settled — the money arrived outside the
// platform. Does not change the subscription; the seats or plan were already
// applied when the invoice was raised. The server refuses a gateway charge
// (its status belongs to Razorpay) and refuses a future date.
export async function settleOfflinePayment(companyId, paymentId, paidOn) {
    const { data } = await axiosInstance.patch(
        `/super-admin/companies/${companyId}/payments/${paymentId}/settle`,
        { paid_on: paidOn }
    );
    return data?.data ?? data;
}

// Operational billing trace — unlike `subscription_timeline` (built only from
// rows that SUCCEEDED), this shows flows that failed midway.
// params: { limit?, level?, flow?, trace_id?, reconcile? }
export async function getCompanyBillingTrace(companyId, params = {}) {
    const { data } = await axiosInstance.get(
        `/super-admin/companies/${companyId}/billing-trace`,
        { params }
    );
    return data?.data ?? data;
}

export async function getRecentBillingFailures(hours = 24) {
    const { data } = await axiosInstance.get('/super-admin/billing-trace/failures', {
        params: { hours },
    });
    return data?.data ?? data;
}

// ── Platform-wide billing (super-admin only, role-enforced server-side) ──────

// params: { from, to, all_time, status, purpose_category, company_id, unlinked,
//           search, skip, limit, summary }
export async function getPlatformPayments(params = {}) {
    const { data } = await axiosInstance.get('/super-admin/payments', { params });
    return data?.data ?? data;
}

export async function getPaymentDetail(paymentId) {
    const { data } = await axiosInstance.get(`/super-admin/payments/${paymentId}`);
    return data?.data ?? data;
}

// params: { hours, grace_minutes, stuck_hours, limit }
export async function getBillingHealth(params = {}) {
    const { data } = await axiosInstance.get('/super-admin/billing-health', { params });
    return data?.data ?? data;
}

// params: { event, flow, company_id, hours, skip, limit }
export async function getBillingHealthErrors(params = {}) {
    const { data } = await axiosInstance.get('/super-admin/billing-health/errors', { params });
    return data?.data ?? data;
}
